import React, { useEffect, useState } from "react";
import { UserActivityEntry, UserEventEntry, useAnalyticsApi } from "./analyticsService";
import { useLanguage } from "../../i18n/LanguageProvider";

export const UserActivityPanel: React.FC = () => {
  const { t, locale } = useLanguage();
  const api = useAnalyticsApi();
  const [days, setDays] = useState(30);
  const [users, setUsers] = useState<UserActivityEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<UserActivityEntry | null>(null);
  const [events, setEvents] = useState<UserEventEntry[] | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.fetchUserActivity(days, 100)
      .then((r) => setUsers(r.users))
      .catch((e) => {
        setUsers([]);
        setError((e as Error)?.message || "Failed to load");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const openUser = (u: UserActivityEntry) => {
    setSelected(u);
    setEvents(null);
    setEventsLoading(true);
    api.fetchUserEventStream(u.userId, 200)
      .then((r) => setEvents(r.events))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  };

  const closeDetail = () => {
    setSelected(null);
    setEvents(null);
  };

  const fmtTime = (ts: string) =>
    new Date(ts).toLocaleString(locale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const initial = (u: UserActivityEntry) =>
    (u.username?.[0] || u.email?.[0] || "?").toUpperCase();

  return (
    <div className="sp-card">
      <div className="sp-card__head">
        <h3 className="sp-card__title">{t("analytics.userActivity")}</h3>
        <select
          className="sp-select"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
        >
          <option value={7}>{t("analytics.lastDays", { count: 7 })}</option>
          <option value={30}>{t("analytics.lastDays", { count: 30 })}</option>
          <option value={90}>{t("analytics.lastDays", { count: 90 })}</option>
        </select>
      </div>

      {loading && (
        <div className="sp-empty">
          <span className="sp-spinner" />
        </div>
      )}

      {!loading && error && (
        <div className="sp-empty">{error}</div>
      )}

      {!loading && !error && users && users.length === 0 && (
        <div className="sp-empty">{t("analytics.noActivity")}</div>
      )}

      {!loading && users && users.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="sp-table">
            <thead>
              <tr>
                <th>{t("analytics.user")}</th>
                <th>{t("analytics.plan")}</th>
                <th className="num">{t("analytics.activeDaysMonth")}</th>
                <th className="num">{t("analytics.apiCallsMonth")}</th>
                <th className="num">{t("analytics.activeDays")}</th>
                <th className="num">{t("analytics.pageViews")}</th>
                <th className="num">{t("analytics.areaToggles")}</th>
                <th className="num">{t("analytics.ctaClicks")}</th>
                <th className="num">{t("analytics.modalOpens")}</th>
                <th>{t("analytics.lastActive")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.userId}
                  className="is-clickable"
                  onClick={() => openUser(u)}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span className="sp-avatar" style={{ width: 28, height: 28, fontSize: "0.78rem" }}>
                        {initial(u)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{u.username || u.email.split("@")[0]}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--sp-text-dim)" }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{u.planName || "—"}</td>
                  <td className="num">
                    {u.maxActiveDaysPerMonth != null
                      ? `${u.activeDaysThisMonth} / ${u.maxActiveDaysPerMonth}`
                      : u.activeDaysThisMonth}
                  </td>
                  <td className="num">
                    {u.apiRequestsPerMonth != null
                      ? `${u.apiCallsThisMonth} / ${u.apiRequestsPerMonth}`
                      : u.apiCallsThisMonth || "—"}
                  </td>
                  <td className="num">{u.activeDays}</td>
                  <td className="num">{u.pageViews}</td>
                  <td className="num">{u.areaToggles}</td>
                  <td className="num">{u.ctaClicks}</td>
                  <td className="num">{u.modalOpens}</td>
                  <td>{u.lastActiveAt ? fmtTime(u.lastActiveAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <>
          <div className="sp-drawer__backdrop" onClick={closeDetail} />
          <aside className="sp-drawer">
            <div className="sp-drawer__head">
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <span className="sp-avatar sp-avatar--lg">{initial(selected)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "1rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selected.username || selected.email.split("@")[0]}
                  </div>
                  <div className="sp-card__sub" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selected.email}
                  </div>
                </div>
              </div>
              <button className="sp-icon-btn" onClick={closeDetail} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            </div>

            {/* Mini stats */}
            <div className="sp-drawer__stats">
              <div>
                <div className="sp-tile__label">{t("analytics.pageViews")}</div>
                <div className="sp-drawer__stat">{selected.pageViews}</div>
              </div>
              <div>
                <div className="sp-tile__label">{t("analytics.areaToggles")}</div>
                <div className="sp-drawer__stat">{selected.areaToggles}</div>
              </div>
              <div>
                <div className="sp-tile__label">{t("analytics.ctaClicks")}</div>
                <div className="sp-drawer__stat">{selected.ctaClicks}</div>
              </div>
              <div>
                <div className="sp-tile__label">{t("analytics.modalOpens")}</div>
                <div className="sp-drawer__stat">{selected.modalOpens}</div>
              </div>
            </div>

            <div className="sp-drawer__body">
              {eventsLoading && (
                <div className="sp-empty">
                  <span className="sp-spinner" />
                </div>
              )}
              {!eventsLoading && events && events.length === 0 && (
                <div className="sp-empty">{t("analytics.noEvents")}</div>
              )}
              {!eventsLoading && events && events.length > 0 && (
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>{t("analytics.event")}</th>
                      <th>{t("analytics.target")}</th>
                      <th>{t("analytics.when")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e, i) => (
                      <tr key={i}>
                        <td>
                          <span className="sp-chip sp-chip--accent">{e.eventType}</span>
                        </td>
                        <td>
                          <code style={{ fontSize: "0.78rem", color: "var(--sp-text-dim)" }}>
                            {e.target || "—"}
                          </code>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{fmtTime(e.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};
